/**
 * §03「つながりの構造」レベルB（個人ネットワーク）デモ用ダミー生成。
 *
 * 設計方針（顧客提示用の完成像確認）:
 *  - 入力イメージ: 回答画面の「ONA具体化(Part5)」の代わりに、所属部署内の同僚を
 *    最大3人までプルダウン選択（ネームジェネレータ）。→ ここではダミーで再現する。
 *  - 部署内(イントラ)の有向エッジ（自分→よく相談する同僚）から、各部署の
 *    キーパーソン（相談が集まる人＝in-degree最大）と、チーム構造（三角形/V字/スポーク）を算出する。
 *  - 実名表示は会社単位フラグ(showRealNames)で出し分ける想定。匿名時はラベル化する。
 *
 * 注: 完全なデモ用。氏名・相談関係はランダム生成のダミー。
 */

export interface ConsultMember {
  id: string;
  name: string; // 実名（ダミー）
  anonLabel: string; // 匿名表示用ラベル（例: 製造-A）
  dept: string;
}

export interface ConsultEdge {
  from: string; // 相談する人
  to: string; // 相談される人
}

export type TeamShape = 'triangle' | 'vee' | 'spoke';

export interface DeptConsultStat {
  dept: string;
  members: ConsultMember[];
  edges: ConsultEdge[];
  inDegree: Record<string, number>;
  /** しきい値方式のキーパーソン（複数可）。被相談数が最大の KP_RATIO 以上 かつ KP_MIN 人以上から頼られる人 */
  keypersonIds: string[];
  /** 部署内の最大被相談数 */
  maxInDegree: number;
  /** 相談の一極集中度 0〜1（高いほどV字依存） */
  concentration: number;
  shape: TeamShape;
  summary: string;
}

/** キーパーソン判定のしきい値: 最大被相談数の何割以上か */
export const KP_RATIO = 0.8;
/** キーパーソンとみなす最小被相談数（1人だけに頼られる人は除外） */
export const KP_MIN = 2;

/** 部署間（dept×dept）の往来強度 0〜1。Part6(onaDept)相当のダミー */
export interface DeptLink {
  a: string;
  b: string;
  strength: number;
  weak: boolean;
}

export interface ConsultNetwork {
  members: ConsultMember[];
  depts: DeptConsultStat[];
  /** 全社のキーパーソン上位（in-degree順） */
  topKeypersons: Array<{ member: ConsultMember; inDegree: number; shape: TeamShape }>;
  /** 部署間リンク（モック統合版の「部署間（弱/強）」表示用） */
  deptLinks: DeptLink[];
}

/** 部署間リンクを「弱」とみなすしきい値 */
export const DEPT_LINK_WEAK = 0.35;

const SURNAMES = [
  '佐藤', '鈴木', '高橋', '田中', '渡辺', '伊藤', '山本', '中村', '小林', '加藤',
  '吉田', '山田', '佐々木', '山口', '松本', '井上', '木村', '林', '清水', '森',
];

// デモの見え方確認用: 姓＋名で氏名を4〜6文字に（名は2〜3文字）
const GIVEN_NAMES = [
  '太郎', '花子', '健一郎', '美咲', '慎之介', '陽菜', '大輔', 'さくら', '直樹', '由美子',
  '智恵子', '真由美', '健太', 'あかり', '涼介', '奈々', '洋平', 'ひかり', '京子', '恵美子',
];

const DEPTS = ['製造部', '営業部', '管理部'];

function randInt(maxExclusive: number): number {
  return Math.floor(Math.random() * maxExclusive);
}

function pickDistinct<T>(arr: T[], k: number): T[] {
  const pool = [...arr];
  const out: T[] = [];
  while (out.length < k && pool.length > 0) {
    out.push(pool.splice(randInt(pool.length), 1)[0]);
  }
  return out;
}

export interface GenerateConsultOptions {
  respondents?: number;
}

export function generateConsultNetwork(opts: GenerateConsultOptions = {}): ConsultNetwork {
  const respondents = Math.max(3, opts.respondents ?? 20);

  // メンバー生成（部署を順番に割当）
  const members: ConsultMember[] = [];
  const deptCounters: Record<string, number> = {};
  for (let i = 0; i < respondents; i++) {
    const dept = DEPTS[i % DEPTS.length];
    deptCounters[dept] = (deptCounters[dept] ?? 0) + 1;
    const suffix = i >= SURNAMES.length ? String(Math.floor(i / SURNAMES.length) + 1) : '';
    const anonChar = String.fromCharCode(64 + deptCounters[dept]); // A,B,C...
    members.push({
      id: `m${i}`,
      name: `${SURNAMES[i % SURNAMES.length]}${GIVEN_NAMES[i % GIVEN_NAMES.length]}${suffix}`,
      anonLabel: `${dept.replace('部', '')}-${anonChar}`,
      dept,
    });
  }

  const byDept = new Map<string, ConsultMember[]>();
  members.forEach((m) => {
    if (!byDept.has(m.dept)) byDept.set(m.dept, []);
    byDept.get(m.dept)!.push(m);
  });

  // 部署内エッジ生成（各自→同部署の最大3人）
  const depts: DeptConsultStat[] = [];
  for (const [dept, dm] of byDept) {
    const edges: ConsultEdge[] = [];
    const inDegree: Record<string, number> = {};
    dm.forEach((m) => (inDegree[m.id] = 0));
    for (const m of dm) {
      const colleagues = dm.filter((c) => c.id !== m.id);
      const k = Math.min(colleagues.length, 1 + randInt(3)); // 1〜3人
      for (const t of pickDistinct(colleagues, k)) {
        edges.push({ from: m.id, to: t.id });
        inDegree[t.id] += 1;
      }
    }

    // キーパーソン（しきい値方式・複数可）
    const maxInDegree = dm.reduce((mx, m) => Math.max(mx, inDegree[m.id]), 0);
    const threshold = Math.max(KP_MIN, Math.ceil(maxInDegree * KP_RATIO));
    const keypersonIds =
      maxInDegree >= KP_MIN ? dm.filter((m) => inDegree[m.id] >= threshold).map((m) => m.id) : [];
    const totalIn = Object.values(inDegree).reduce((a, b) => a + b, 0) || 1;
    const concentration = maxInDegree / totalIn;

    // 構造分類: 0人=スポーク / 1人=V字(一極集中) / 2人以上=三角形(複数で支え合い)
    let shape: TeamShape;
    if (keypersonIds.length === 0) shape = 'spoke';
    else if (keypersonIds.length === 1) shape = 'vee';
    else shape = 'triangle';

    const kpNames = keypersonIds
      .map((id) => dm.find((m) => m.id === id)?.name)
      .filter((n): n is string => Boolean(n));
    const summary =
      shape === 'vee'
        ? `${kpNames[0]}さんに相談が集中（${maxInDegree}人が依存）。${dept}は属人化（V字）のリスクがあり、その人が抜けると回りにくくなります。`
        : shape === 'triangle'
          ? `${kpNames.join('・')}さんに相談が集まっています（複数のキーパーソン）。一極集中ではなく多極で、1人依存よりは安定しています。ただしこの数名に負荷が偏ります。`
          : `${dept}は相談関係が薄く、連携が限定的です。相談し合う相手を増やすと安定します。`;

    depts.push({
      dept,
      members: dm,
      edges,
      inDegree,
      keypersonIds,
      maxInDegree,
      concentration,
      shape,
      summary,
    });
  }

  // 全社キーパーソン一覧（部署をまたいで全キーパーソンを被相談数順に）
  const topKeypersons = depts
    .flatMap((d) =>
      d.keypersonIds.map((id) => ({
        member: d.members.find((m) => m.id === id)!,
        inDegree: d.inDegree[id],
        shape: d.shape,
      }))
    )
    .sort((a, b) => b.inDegree - a.inDegree);

  // 部署間リンク（ダミー。Part6 onaDept 相当）
  const deptNames = depts.map((d) => d.dept);
  const deptLinks: DeptLink[] = [];
  for (let i = 0; i < deptNames.length; i++) {
    for (let j = i + 1; j < deptNames.length; j++) {
      const strength = Math.round((0.12 + Math.random() * 0.68) * 100) / 100;
      deptLinks.push({
        a: deptNames[i],
        b: deptNames[j],
        strength,
        weak: strength < DEPT_LINK_WEAK,
      });
    }
  }

  return { members, depts, topKeypersons, deptLinks };
}
